import React, { useEffect, useRef, useState } from "react";
import { api, apiErr } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input, Label, Textarea } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { toast } from "sonner";
import { Film, Music, ImageIcon, FileText, Download, Pencil, Trash2, Upload, Eye, ShieldCheck } from "lucide-react";
import { PageShell } from "./PageShell";

const fmtSize = (b) => (b > 1024 * 1024 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`);
const TYPE_ICON = { video: Film, audio: Music, image: ImageIcon, document: FileText };

async function fetchBlobUrl(id, download = false) {
  const r = await api.get(`/media/assets/${id}/file${download ? "?download=1" : ""}`, { responseType: "blob" });
  return URL.createObjectURL(r.data);
}

function AssetCard({ a, onPreview, onRename, onDelete }) {
  const [thumb, setThumb] = useState(null);
  useEffect(() => {
    let url;
    if (a.type === "image") fetchBlobUrl(a.id).then((u) => { url = u; setThumb(u); }).catch(() => {});
    return () => url && URL.revokeObjectURL(url);
  }, [a.id, a.type]);
  const Icon = TYPE_ICON[a.type] || Film;
  const dims = a.meta?.width ? `${a.meta.width}×${a.meta.height}` : null;
  const dur = a.meta?.duration_s ? `${a.meta.duration_s}s` : null;

  const download = async () => {
    try {
      const u = await fetchBlobUrl(a.id, true);
      const el = document.createElement("a");
      el.href = u; el.download = a.filename; el.click();
      URL.revokeObjectURL(u);
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="card-flat overflow-hidden flex flex-col" data-testid={`media-card-${a.id}`}>
      <button className="h-36 bg-surface-hover flex items-center justify-center overflow-hidden group relative" onClick={() => onPreview(a)} data-testid={`media-preview-${a.id}`}>
        {thumb ? <img src={thumb} alt={a.title} className="h-full w-full object-contain p-2" />
          : <Icon className="h-10 w-10 text-muted-foreground" />}
        <span className="absolute inset-0 bg-black/0 group-hover:bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <Eye className="h-5 w-5 text-white" />
        </span>
      </button>
      <div className="p-3 flex-1 flex flex-col gap-1.5">
        <div className="text-xs font-semibold leading-snug" data-testid={`media-title-${a.id}`}>{a.title}</div>
        {a.description && <div className="text-[10px] text-muted-foreground leading-snug">{a.description}</div>}
        <div className="flex flex-wrap gap-1 mt-auto pt-1.5">
          <Badge variant="outline" className="text-[8px] uppercase">{a.format}</Badge>
          {dims && <Badge variant="outline" className="text-[8px]">{dims}</Badge>}
          {dur && <Badge variant="outline" className="text-[8px]">{dur}</Badge>}
          <Badge variant="outline" className="text-[8px]">{fmtSize(a.size_bytes)}</Badge>
          {a.system && <Badge variant="lime" className="text-[8px] flex items-center gap-0.5"><ShieldCheck className="h-2.5 w-2.5" /> system</Badge>}
        </div>
        <div className="text-[9px] text-muted-foreground font-mono">{new Date(a.created_at).toLocaleDateString()} · {a.uploaded_by}</div>
        <div className="flex gap-1 pt-1">
          <Button size="sm" variant="outline" className="h-6 text-[10px] flex-1" onClick={download} data-testid={`media-download-${a.id}`}><Download className="h-3 w-3" /> Download</Button>
          <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => onRename(a)} data-testid={`media-rename-${a.id}`}><Pencil className="h-3 w-3" /></Button>
          <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => onDelete(a)} data-testid={`media-delete-${a.id}`}><Trash2 className="h-3 w-3 text-red-400" /></Button>
        </div>
      </div>
    </div>
  );
}

function PreviewDialog({ a, onClose }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let u;
    fetchBlobUrl(a.id).then((x) => { u = x; setUrl(x); }).catch((e) => toast.error(apiErr(e)));
    return () => u && URL.revokeObjectURL(u);
  }, [a.id]);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="media-preview-dialog">
        <DialogHeader><DialogTitle className="text-sm">{a.title}</DialogTitle></DialogHeader>
        {!url ? <div className="text-xs text-muted-foreground py-10 text-center">Loading…</div>
          : a.type === "video" ? <video src={url} controls autoPlay className="w-full rounded-sm max-h-[60vh] bg-black" data-testid="media-preview-video" />
          : a.type === "audio" ? <audio src={url} controls autoPlay className="w-full" data-testid="media-preview-audio" />
          : a.type === "document" ? <iframe src={url} title={a.title} className="w-full h-[70vh] rounded-sm bg-white" data-testid="media-preview-document" />
          : <img src={url} alt={a.title} className="w-full max-h-[60vh] object-contain rounded-sm bg-surface-hover" data-testid="media-preview-image" />}
      </DialogContent>
    </Dialog>
  );
}

const CHUNK = 2 * 1024 * 1024;

export default function PlatformMedia() {
  const [assets, setAssets] = useState([]);
  const [preview, setPreview] = useState(null);
  const [rename, setRename] = useState(null);
  const [upOpen, setUpOpen] = useState(false);
  const [upTitle, setUpTitle] = useState("");
  const [upDesc, setUpDesc] = useState("");
  const [upFile, setUpFile] = useState(null);
  const [upPct, setUpPct] = useState(null);
  const fileRef = useRef(null);

  const load = () => api.get("/media/assets").then((r) => setAssets(r.data.assets)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); }, []);

  const doUpload = async () => {
    if (!upFile) return toast.error("Choose a file first");
    setUpPct(0);
    try {
      const init = await api.post("/media/upload/init", { filename: upFile.name });
      const { upload_id } = init.data;
      const total = Math.ceil(upFile.size / CHUNK);
      for (let i = 0; i < total; i++) {
        const fd = new FormData();
        fd.append("index", i);
        fd.append("chunk", upFile.slice(i * CHUNK, (i + 1) * CHUNK));
        await api.post(`/media/upload/${upload_id}/chunk`, fd);
        setUpPct(Math.round(((i + 1) / (total + 1)) * 100));
      }
      await api.post(`/media/upload/${upload_id}/complete`, { filename: upFile.name, title: upTitle || upFile.name, description: upDesc });
      toast.success("Uploaded to the media repository");
      setUpOpen(false); setUpFile(null); setUpTitle(""); setUpDesc(""); setUpPct(null);
      load();
    } catch (e) { toast.error(apiErr(e)); setUpPct(null); }
  };

  const doRename = async () => {
    try {
      await api.patch(`/media/assets/${rename.id}`, { title: rename.title, description: rename.description || "" });
      toast.success("Saved"); setRename(null); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const doDelete = async (a) => {
    if (!window.confirm(`Delete "${a.title}"?`)) return;
    try {
      await api.delete(`/media/assets/${a.id}`);
      toast.success("Deleted"); load();
    } catch (e) {
      if (e.response?.status === 409) {
        if (window.confirm("This is a protected SYSTEM asset (used by the platform). Delete it anyway?")) {
          try { await api.delete(`/media/assets/${a.id}?force=1`); toast.success("System asset deleted"); load(); }
          catch (e2) { toast.error(apiErr(e2)); }
        }
      } else toast.error(apiErr(e));
    }
  };

  return (
    <PageShell title="Media Repository" subtitle="Brand and marketing assets — pitch videos, voiceovers, scenes and logos. Downloads are authenticated; nothing here is publicly linkable." testid="platform-media-page"
      actions={<Button variant="primary" size="sm" onClick={() => setUpOpen(true)} data-testid="media-upload-btn"><Upload className="h-3.5 w-3.5" /> Upload asset</Button>}>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="media-grid">
        {assets.map((a) => <AssetCard key={a.id} a={a} onPreview={setPreview} onRename={(x) => setRename({ ...x })} onDelete={doDelete} />)}
        {assets.length === 0 && <div className="col-span-full card-flat p-10 text-center text-sm text-muted-foreground">No assets yet.</div>}
      </div>

      {preview && <PreviewDialog a={preview} onClose={() => setPreview(null)} />}

      <Dialog open={!!rename} onOpenChange={(o) => !o && setRename(null)}>
        <DialogContent data-testid="media-rename-dialog">
          <DialogHeader><DialogTitle>Edit asset</DialogTitle></DialogHeader>
          {rename && (
            <div className="space-y-3">
              <div><Label>Title</Label><Input value={rename.title} onChange={(e) => setRename({ ...rename, title: e.target.value })} data-testid="media-rename-title" /></div>
              <div><Label>Description</Label><Textarea rows={2} value={rename.description || ""} onChange={(e) => setRename({ ...rename, description: e.target.value })} data-testid="media-rename-desc" /></div>
            </div>
          )}
          <div className="flex justify-end"><Button variant="primary" size="sm" onClick={doRename} data-testid="media-rename-save">Save</Button></div>
        </DialogContent>
      </Dialog>

      <Dialog open={upOpen} onOpenChange={(o) => { if (!o && upPct === null) setUpOpen(false); }}>
        <DialogContent data-testid="media-upload-dialog">
          <DialogHeader><DialogTitle>Upload asset</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>File (image / video / audio / PDF)</Label>
              <input ref={fileRef} type="file" accept="image/*,video/*,audio/*,application/pdf,.pdf" data-testid="media-upload-file"
                className="block w-full text-xs mt-1 file:mr-3 file:px-3 file:py-1.5 file:rounded-sm file:border-0 file:bg-accent file:text-accent-foreground file:text-xs file:font-semibold"
                onChange={(e) => setUpFile(e.target.files?.[0] || null)} />
              {upFile && <div className="text-[10px] text-muted-foreground mt-1 font-mono">{upFile.name} · {fmtSize(upFile.size)}</div>}
            </div>
            <div><Label>Title</Label><Input value={upTitle} onChange={(e) => setUpTitle(e.target.value)} placeholder={upFile?.name || "Asset title"} data-testid="media-upload-title" /></div>
            <div><Label>Description (optional)</Label><Textarea rows={2} value={upDesc} onChange={(e) => setUpDesc(e.target.value)} data-testid="media-upload-desc" /></div>
            {upPct !== null && (
              <div className="h-2 rounded-full bg-surface-hover overflow-hidden" data-testid="media-upload-progress">
                <div className="h-full bg-accent transition-all" style={{ width: `${upPct}%` }} />
              </div>
            )}
          </div>
          <div className="flex justify-end">
            <Button variant="primary" size="sm" onClick={doUpload} disabled={upPct !== null} data-testid="media-upload-submit">
              {upPct !== null ? `Uploading… ${upPct}%` : "Upload"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
}
